/**
 * CreateRoleModal — unified modal system.
 * Fields: code, name, nameEn, description, color, icon.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldIcon, HashIcon, PaletteIcon, StickyNoteIcon, SmileIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useCreateRole } from '../hooks';
import type { RoleFormData } from '../types';

interface CreateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateRoleModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateRoleModalProps) {
  const { t } = useTranslation();
  const createRole = useCreateRole();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'NEW ROLE',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#1565c0');
  const [icon, setIcon] = useState('Shield');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setName('');
      setNameEn('');
      setDescription('');
      setColor('#1565c0');
      setIcon('Shield');
      setError('');
    }
  }, [isOpen]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) {
      setError(t('validation.codeRequired'));
      focusField('cr-code');
      return false;
    }
    if (!name.trim()) {
      setError(t('validation.nameRequired'));
      focusField('cr-name');
      return false;
    }
    return true;
  };

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim());
  }, [code, name]);

  const handleSubmit = () => {
    const payload: RoleFormData = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      description: description.trim() || undefined,
      color: color.trim() || undefined,
      icon: icon.trim() || undefined,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () => createRole.mutateAsync(payload),
      onSuccess: () => {
        toast.success(t('rolesAdmin.createSuccess'));
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        if (msg.includes('already exists')) {
          setError(t('rolesAdmin.codeExists'));
        } else {
          setError(err.response?.data?.error || msg || t('rolesAdmin.createFailed'));
        }
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('rolesAdmin.createTitle')}
      isPending={createRole.isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="NEW ROLE"
      error={error}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<HashIcon className="h-4 w-4" />} label={t('rolesAdmin.code')} required>
            <input
              id="cr-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="SUPERVISOR"
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<ShieldIcon className="h-4 w-4" />} label={t('rolesAdmin.name')} required>
            <input
              id="cr-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
        <FormField icon={<ShieldIcon className="h-4 w-4" />} label={t('rolesAdmin.nameEn')}>
          <input
            id="cr-nameEn"
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder="e.g. Supervisor"
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>
        <FormField icon={<StickyNoteIcon className="h-4 w-4" />} label={t('rolesAdmin.description')}>
          <textarea
            id="cr-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' as const }}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<PaletteIcon className="h-4 w-4" />} label={t('rolesAdmin.color')}>
            <div className="flex items-center gap-2">
              <input
                id="cr-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 rounded-lg cursor-pointer border border-gray-200"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#1565c0"
                maxLength={7}
                className="flex-1 p-2.5 rounded-lg font-mono font-semibold text-base focus:outline-none focus:ring-2"
                style={inputStyle}
              />
            </div>
          </FormField>
          <FormField icon={<SmileIcon className="h-4 w-4" />} label={t('rolesAdmin.icon')}>
            <select
              id="cr-icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
              style={inputStyle}
            >
              <option value="Shield">Shield</option>
              <option value="ShieldAlert">ShieldAlert</option>
              <option value="ShieldCheck">ShieldCheck</option>
              <option value="Users">Users</option>
              <option value="User">User</option>
              <option value="UserCog">UserCog</option>
              <option value="Crown">Crown</option>
              <option value="Star">Star</option>
              <option value="Eye">Eye</option>
              <option value="Settings">Settings</option>
            </select>
          </FormField>
        </div>
      </div>
    </FormModal>
  );
}
